/**
 * Format an amount in cents to a display string with currency.
 * @param cents Amount in cents
 * @param currency ISO currency code (defaults to "usd")
 * @returns Formatted string e.g. "12.50 USD" or "-" for null/undefined
 */
export const formatAmount = (
  cents: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (cents === null || cents === undefined) return "-";
  const curr = (currency || "USD").toUpperCase();
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
  return `${formatted} ${curr}`;
};

export type AppCurrency = "cad" | "usd" | "aud";

export const getCurrencySymbol = (currency: AppCurrency): string => {
  switch (currency) {
    case "aud":
      return "A$";
    case "cad":
      return "C$";
    case "usd":
    default:
      return "$";
  }
};

/**
 * Format an amount (in dollars) with a currency symbol prefix.
 * @param amount Amount in dollars (not cents)
 * @param currency Currency code
 * @returns Formatted string e.g. "$12.50"
 */
export const formatCurrency = (
  amount: number,
  currency: AppCurrency,
): string => {
  const symbol = getCurrencySymbol(currency);
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${symbol}${formatted}`;
};

/**
 * Format an amount (in dollars) with ISO currency code suffix.
 * @param amount Amount in dollars (not cents)
 * @param currency Currency code
 * @returns Formatted string e.g. "12.50 CAD"
 */
export const formatCurrencyISO = (
  amount: number,
  currency: AppCurrency,
): string => {
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${formatted} ${currency.toUpperCase()}`;
};
